/* Geometry/visibility only: never replace theme.Navigation, search, cart or auth. */
(() => {
  'use strict';
  if (!('AbortController' in window) || !('ResizeObserver' in window)) return;
  if (window.lkHeaderParity) { window.lkHeaderParity.mount(); return; }
  const html = document.documentElement;
  const lifecycle = new AbortController();
  let dispose;
  let mountFrame;

  function mount() {
    if (dispose) dispose();
    const header = document.querySelector('[data-lk-header-parity]');
    if (!header) return;
    const section = header.closest('.section-header');
    if (!section) return;
    const announcement = document.querySelector('.section-announcement-bar .lk-shell-announcement');
    const announcementSection = announcement && announcement.closest('.section-announcement-bar');
    const events = new AbortController();
    const listen = (target, type, handler, options = {}) => target.addEventListener(type, handler, {...options, signal: events.signal});
    let frame;
    let lastY = Math.max(0, window.scrollY);
    const priorProperties = ['--theme-header-height', '--theme-sticky-header-height'].map(name => [name, html.style.getPropertyValue(name)]);

    function show() {
      header.classList.remove('lk-header-hidden');
      if (announcement) announcement.classList.remove('lk-header-hidden');
    }
    function locked() {
      // Native theme uses body classes, header classes, and an inserted cart overlay.
      const state = '.reveal-mobile-nav, .enable-mobile-nav-transition, .show-search, .section-header--nav-open, .section-header--nav-hover, .cart-drawer-open, .cart-open, .show-cart';
      return html.matches(state) || document.body.matches(state) || section.matches(state) ||
        !!document.querySelector('.cart-summary-overlay, dialog[open], [aria-modal="true"]') ||
        header.contains(document.activeElement) || (announcement && announcement.contains(document.activeElement));
    }
    function measure() {
      const nativeReady = !!(window.theme && window.theme.openMobileNav && document.querySelector('.lk-shell-mobile'));
      html.classList.toggle('lk-header-native-ready', nativeReady);
      if (window.theme && window.theme.inlineNavigationCheck) window.theme.inlineNavigationCheck();
      const headerHeight = Math.ceil(header.getBoundingClientRect().height);
      const announcementHeight = announcement ? Math.ceil(announcement.getBoundingClientRect().height) : 0;
      const total = headerHeight + announcementHeight;
      html.style.setProperty('--lk-header-flow-height', `${headerHeight}px`);
      html.style.setProperty('--lk-announcement-flow-height', `${announcementHeight}px`);
      html.style.setProperty('--lk-header-total-height', `${total}px`);
      // Keep the native header-only property semantics; sticky offset includes both rows.
      html.style.setProperty('--theme-header-height', `${headerHeight}px`);
      html.style.setProperty('--theme-sticky-header-height', `${total}px`);
    }
    function scroll() {
      const y = Math.max(0, window.scrollY);
      if (locked() || y <= 50 || y < lastY) show();
      else if (y > lastY) {
        header.classList.add('lk-header-hidden');
        if (announcement) announcement.classList.add('lk-header-hidden');
      }
      lastY = y;
    }
    function queueMeasure() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => { measure(); if (locked()) show(); });
    }

    html.classList.add('lk-header-parity-active');
    measure(); // Read normal flow before fixing; wrappers reserve exactly the same space.
    section.classList.add('lk-header-placeholder');
    header.setAttribute('data-lk-header-fixed', '');
    if (announcement) {
      announcementSection.classList.add('lk-announcement-placeholder');
      announcement.setAttribute('data-lk-announcement-fixed', '');
    }
    const sizes = new ResizeObserver(queueMeasure);
    sizes.observe(header);
    if (announcement) sizes.observe(announcement);
    const state = new MutationObserver(() => { if (locked()) show(); });
    state.observe(html, {attributes: true, attributeFilter: ['class', 'dir']});
    state.observe(document.body, {attributes: true, attributeFilter: ['class'], childList: true});
    state.observe(section, {attributes: true, attributeFilter: ['class']});
    listen(window, 'scroll', scroll, {passive: true});
    listen(window, 'resize', () => { show(); queueMeasure(); }, {passive: true});
    listen(document, 'focusin', () => { if (locked()) show(); });
    listen(document, 'click', () => { if (locked()) show(); });
    // The inline row is a native proxy; route keyboard focus through that same native path.
    listen(header, 'focusin', event => {
      const item = event.target.closest('.navigation--left .navigation__item--with-children');
      if (item && window.jQuery) window.jQuery(item).trigger('mouseenter');
    });
    listen(header, 'focusout', event => {
      if (header.contains(event.relatedTarget)) return;
      if (window.jQuery) window.jQuery(header).find('.navigation__item--show-children').trigger('mouseleave');
    });
    listen(header, 'keydown', event => {
      if (event.key === 'Escape' && !document.body.classList.contains('show-search') && window.jQuery) {
        window.jQuery(header).find('.navigation__item--show-children').trigger('mouseleave');
      }
    });
    // Native debounced resize runs later and writes header-only sticky height.
    if (window.jQuery) window.jQuery(window).on('debouncedresize.lkHeaderParity', queueMeasure);
    if (document.fonts) document.fonts.ready.then(() => { if (!events.signal.aborted) queueMeasure(); });
    queueMeasure(); // jQuery's ready callback may create the native drawer after DOMContentLoaded.

    dispose = () => {
      events.abort();
      cancelAnimationFrame(frame);
      sizes.disconnect();
      state.disconnect();
      if (window.jQuery) window.jQuery(window).off('.lkHeaderParity');
      show();
      header.removeAttribute('data-lk-header-fixed');
      section.classList.remove('lk-header-placeholder');
      if (announcement) {
        announcement.removeAttribute('data-lk-announcement-fixed');
        announcementSection.classList.remove('lk-announcement-placeholder');
      }
      html.classList.remove('lk-header-parity-active', 'lk-header-native-ready');
      ['--lk-header-flow-height', '--lk-announcement-flow-height', '--lk-header-total-height'].forEach(name => html.style.removeProperty(name));
      priorProperties.forEach(([name, value]) => value ? html.style.setProperty(name, value) : html.style.removeProperty(name));
      dispose = undefined;
    };
  }
  function queueMount() {
    cancelAnimationFrame(mountFrame);
    mountFrame = requestAnimationFrame(mount);
  }
  window.lkHeaderParity = {mount: queueMount};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', queueMount, {once: true, signal: lifecycle.signal});
  else queueMount();
  document.addEventListener('shopify:section:load', event => {
    if (event.target.matches('.section-header, .section-announcement-bar') || event.target.querySelector('[data-lk-header-parity], .lk-shell-announcement')) queueMount();
  }, {signal: lifecycle.signal});
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches('.section-header, .section-announcement-bar')) {
      cancelAnimationFrame(mountFrame);
      if (dispose) dispose();
    }
  }, {signal: lifecycle.signal});
})();